import { keyBy, partition } from 'lodash'
import { envRefs } from '@wix/thunderbolt-catharsis'
import type {
	BaseDataItem,
	Component,
	PresetData,
	InnerElementsMap,
	ResolvedBuilderWidgetDataItem,
	ReducedManifest,
} from '@wix/thunderbolt-becky-types'
import type { FeatureRefs } from '../cssFeatures.types'
import { createCssCompNode } from '../cssCompNode'
import { isRefArray, isVariantRelation } from '../../utils/variantsUtils'
import { isBreakpointRange } from '../../utils/is'
import type { BpToPresetsCss } from './presets.types'

// TODO: decide on data type
const isPresetDataItem = (presetVariant: BaseDataItem) => {
	if (presetVariant.type === 'PresetData') {
		return true
	}
	if (presetVariant.type.endsWith('.Presets')) {
		return true
	}

	return false
}

const getPresetData = (presetVariant: BaseDataItem) => {
	if (isPresetDataItem(presetVariant)) {
		return {
			presetName: (presetVariant as PresetData).presetName,
			variants: [],
		}
	}
	if (isVariantRelation(presetVariant)) {
		return {
			presetName: (presetVariant.to as PresetData).presetName,
			variants: presetVariant.variants,
		}
	}
}

const getPresetKeysFromReducedManifest = ({
	reducedManifest,
	path,
	presetName,
}: {
	reducedManifest: ReducedManifest
	path: string
	presetName: string
}): {
	presetVariableName: string
	customCssKey: string
} => {
	const { presetVariableKey, presets } = reducedManifest.elementData[path]

	return {
		presetVariableName: presetVariableKey ? `--preset-${presetVariableKey}` : '',
		customCssKey: presets?.[presetName]?.customCssKey || presetName,
	}
}

const generateInnerElements = (
	innerElementsDataItem: ResolvedBuilderWidgetDataItem,
	parentKey: string
): InnerElementsMap => {
	const result: InnerElementsMap = {}
	const innerElements = innerElementsDataItem?.innerElements

	if (!innerElements) {
		return result
	}

	for (const innerElementName in innerElements) {
		if (typeof innerElements[innerElementName] === 'object') {
			const combinedKey = `${parentKey}/${innerElementName}`

			if ('presetsQuery' in innerElements[innerElementName]) {
				result[combinedKey] = innerElements[innerElementName]
			}

			if ('innerElementsQuery' in innerElements[innerElementName]) {
				const nestedResult = generateInnerElements(
					innerElements[innerElementName].innerElementsQuery,
					combinedKey
				)
				Object.assign(result, nestedResult)
			}
		}
	}

	return result
}

const generatePresetsCssByBp = (
	elementPath: string,
	presetsData: BaseDataItem<any>,
	reducedManifest: ReducedManifest
): BpToPresetsCss => {
	const presetsRefArray = isRefArray(presetsData) ? presetsData.values : [presetsData]
	const presetsAcc: BpToPresetsCss = {}

	return presetsRefArray.reduce((acc: BpToPresetsCss, preset) => {
		const presetData = getPresetData(preset)
		if (!presetData) {
			return acc
		}
		const { presetName, variants } = presetData
		const [[breakpointVariant], variantsWithoutBp] = partition(variants, (variant) => isBreakpointRange(variant))

		const { presetVariableName, customCssKey } = getPresetKeysFromReducedManifest({
			reducedManifest,
			path: elementPath,
			presetName: presetName as string,
		})

		const fallbackPresetCss = `--preset: ${presetName}`

		const breakpointId = breakpointVariant?.id || 'default'
		acc[breakpointId] = acc[breakpointId] || []
		acc[breakpointId].push({
			presetCss: presetVariableName
				? `${presetVariableName}: ${customCssKey}; ${fallbackPresetCss}`
				: fallbackPresetCss,
			variants: keyBy(variantsWithoutBp, 'id'),
		})

		return acc
	}, presetsAcc)
}

export const presets = createCssCompNode('presets', 'presets', {
	getDependencies: (component: Component, refs: FeatureRefs<'presets'>) => {
		if (!component.builderType) {
			return null
		}
		return {
			rootPresetsData: component.presetsQuery ? refs.presetsQuery(component.presetsQuery) : undefined,
			builderTypeToReducedManifest: envRefs.builderTypeToReducedManifest,
			innerElementsDataItem: component.innerElementsQuery
				? refs.innerElementsQuery(component.innerElementsQuery)
				: undefined,
		}
	},
	toViewItem: (component, deps) => {
		if (!component.builderType || !deps) {
			return null
		}
		const { rootPresetsData, innerElementsDataItem, builderTypeToReducedManifest } = deps

		const reducedManifest = builderTypeToReducedManifest?.[component.builderType]
		if (!reducedManifest) {
			return null
		}

		const selectorToPresets: { [selector: string]: BpToPresetsCss } = {}

		if (rootPresetsData) {
			selectorToPresets.root = generatePresetsCssByBp('root', rootPresetsData, reducedManifest)
		}
		if (innerElementsDataItem) {
			const innerElements = generateInnerElements(innerElementsDataItem, 'root')

			for (const innerElementPath in innerElements) {
				const innerElementSelector = reducedManifest.elementData[innerElementPath].selector
				const innerPresetsData = innerElements[innerElementPath].presetsQuery as BaseDataItem<any>

				const innerPresetsByBp = generatePresetsCssByBp(innerElementPath, innerPresetsData, reducedManifest)

				selectorToPresets[innerElementSelector] = innerPresetsByBp
			}
		}

		return selectorToPresets
	},
})
